import { Button, Group, Stack } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { TextSerif } from "@/components/elements";
import { useDeleteGarage } from "@/services/garages/delete-garage";
import { useUpdateStockImagesVisibility } from "@/services/profile/update-stock-images-visibility";
import { showCustomNotification } from "@/utils/notifications";

import type { Garage } from "@/types/garage";
import type { MantineStyleProps } from "@mantine/core";
import type { FC } from "react";

export type DeleteGarageProps = {
  autoCloseDelay?: boolean | number;
  garage: Garage;
  onClose?: () => void;
  onSuccess?: (garageId: string) => void;
} & MantineStyleProps;

export const DeleteGarage: FC<DeleteGarageProps> = ({
  autoCloseDelay = false,
  garage,
  onClose,
  onSuccess,
  ...props
}) => {
  const { t } = useTranslation();
  const { mutate, isPending } = useDeleteGarage({
    mutationConfig: { onSuccess: handleSuccess },
  });
  const { mutateAsync: updateStockImagesVisibility } = useUpdateStockImagesVisibility();

  async function handleSuccess() {
    try {
      for (const carId of garage.data.belongs_to_car ?? []) {
        await updateStockImagesVisibility({ carId, hidden: false });
      }
    } catch (error) {
      console.error("Error removing deleted garage cars from hidden stock images:", error);
    }

    showCustomNotification({
      autoClose: autoCloseDelay,
      title: t("components.DeleteGarage.success.title"),
      message: t("components.DeleteGarage.success.message"),
    });
    onClose?.();
    onSuccess?.(garage.identifier);
  }

  function handleSubmit() {
    mutate({ garageId: garage.identifier });
  }

  return (
    <Stack {...props}>
      <TextSerif style={{ whiteSpace: "pre-line" }}>
        {t("components.DeleteGarage.message")}
      </TextSerif>
      <Group justify="center" mt="lg">
        {onClose && (
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isPending}
            aria-label={t("components.Form.cancelButton")}
          >
            {t("components.Form.cancelButton")}
          </Button>
        )}
        <Button
          loading={isPending}
          onClick={handleSubmit}
          aria-label={t("components.DeleteGarage.submitButton")}
        >
          {t("components.DeleteGarage.submitButton")}
        </Button>
      </Group>
    </Stack>
  );
};
